import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import Select from "react-select"
import { baseUrl } from "../../config/baseUrl"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faSave, faTicketAlt } from "@fortawesome/free-solid-svg-icons"

const COUPON_FOR_OPTIONS = [
    { value: "all", label: "All" },
    { value: "package", label: "Package" },
    { value: "daily_mock", label: "Daily Mock" },
]

const APPLICABLE_ON_OPTIONS = [
    { value: "all", label: "All" },
    { value: "selected", label: "Selected" },
]

const getInitialFormValues = () => ({
    title: "",
    couponCode: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    maxDiscount: "",
    minimumOrderPackage: 0,
    applicableOn: "all",
    couponFor: "all",
    packages: [],
    validFrom: "",
    validTill: "",
    usageLimit: "",
    perUserLimit: 1,
    status: "active",
})

const formatToDatetimeLocal = (isoString) => {
    if (!isoString) return ""

    const date = new Date(isoString)
    const tzOffset = date.getTimezoneOffset() * 60000

    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16)
}

const getRefId = (value) => {
    if (!value) return ""

    if (typeof value === "string") {
        return value
    }

    return value._id || ""
}

const buildTargetEntry = (couponFor, targetId) => ({
    packageId: couponFor === "package" ? targetId : null,
    dailyMockCategoryId: couponFor === "daily_mock" ? targetId : null,
})

const normalizeTargetEntries = (entries, couponFor) => {
    if (!Array.isArray(entries) || !entries.length) {
        return []
    }

    const normalizedEntries = []
    const seen = new Set()

    entries.forEach((entry) => {
        let targetId = ""

        if (typeof entry === "string") {
            targetId = entry
        } else if (entry && typeof entry === "object") {
            targetId =
                getRefId(entry.packageId) ||
                getRefId(entry.dailyMockCategoryId) ||
                getRefId(entry)
        }

        if (!targetId || seen.has(targetId)) {
            return
        }

        seen.add(targetId)
        normalizedEntries.push(buildTargetEntry(couponFor, targetId))
    })

    return normalizedEntries
}

const getSelectedTargetIds = (entries, couponFor) => {
    if (!Array.isArray(entries)) {
        return []
    }

    return entries
        .map((entry) =>
            couponFor === "daily_mock"
                ? getRefId(entry?.dailyMockCategoryId)
                : getRefId(entry?.packageId),
        )
        .filter(Boolean)
}

export default function CouponFormPage({ mode = "create", couponId = "" }) {
    const navigate = useNavigate()
    const isEditMode = mode === "edit"

    const [loading, setLoading] = useState(false)
    const [fetchLoading, setFetchLoading] = useState(true)
    const [packagesList, setPackagesList] = useState([])
    const [dailyMockCategories, setDailyMockCategories] = useState([])
    const [formval, setFormval] = useState(getInitialFormValues)

    const packageOptions = useMemo(
        () =>
            packagesList.map((pkg) => ({
                value: pkg._id,
                label: pkg.title,
            })),
        [packagesList],
    )

    const dailyMockCategoryOptions = useMemo(
        () =>
            dailyMockCategories.map((category) => ({
                value: category._id,
                label: category.title,
            })),
        [dailyMockCategories],
    )

    const targetOptions =
        formval.couponFor === "daily_mock"
            ? dailyMockCategoryOptions
            : packageOptions

    const selectedTargetIds = useMemo(
        () => getSelectedTargetIds(formval.packages, formval.couponFor),
        [formval.packages, formval.couponFor],
    )

    const selectedTargetOptions = targetOptions.filter((option) =>
        selectedTargetIds.includes(option.value),
    )

    const requiresSpecificSelection =
        formval.couponFor !== "all" && formval.applicableOn === "selected"

    const selectionLabel =
        formval.couponFor === "daily_mock"
            ? "Daily Mock Categories"
            : "Packages"

    const selectionItemLabel =
        formval.couponFor === "daily_mock" ? "daily mock category" : "package"

    const loadReferenceData = async () => {
        const [packagesResponse, categoriesResponse] = await Promise.all([
            axios.get(`${baseUrl}/package`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            }),
            axios.get(`${baseUrl}/daily/mock/category`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            }),
        ])

        setPackagesList(packagesResponse.data?.data || [])
        setDailyMockCategories(categoriesResponse.data?.data || [])
    }

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setFetchLoading(true)
                await loadReferenceData()

                if (!isEditMode || !couponId) {
                    return
                }

                const couponResponse = await axios.get(`${baseUrl}/coupon/${couponId}`, {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                    },
                })

                if (couponResponse.status === 200 || couponResponse.data?.status) {
                    const couponData = couponResponse.data.data || {}
                    const couponFor = couponData.couponFor || "all"
                    const applicableOn =
                        couponFor === "all"
                            ? "all"
                            : couponData.applicableOn || "all"

                    setFormval({
                        title: couponData.title || "",
                        couponCode: couponData.couponCode || "",
                        description: couponData.description || "",
                        discountType: couponData.discountType || "percentage",
                        discountValue:
                            couponData.discountValue === undefined ||
                                couponData.discountValue === null
                                ? ""
                                : String(couponData.discountValue),
                        maxDiscount:
                            couponData.maxDiscount === undefined ||
                                couponData.maxDiscount === null
                                ? ""
                                : String(couponData.maxDiscount),
                        minimumOrderPackage:
                            couponData.minimumOrderPackage === undefined ||
                                couponData.minimumOrderPackage === null
                                ? 0
                                : couponData.minimumOrderPackage,
                        applicableOn,
                        couponFor,
                        packages: normalizeTargetEntries(couponData.packages, couponFor),
                        validFrom: formatToDatetimeLocal(couponData.validFrom),
                        validTill: formatToDatetimeLocal(couponData.validTill),
                        usageLimit:
                            couponData.usageLimit === undefined ||
                                couponData.usageLimit === null
                                ? ""
                                : String(couponData.usageLimit),
                        perUserLimit:
                            couponData.perUserLimit === undefined ||
                                couponData.perUserLimit === null
                                ? 1
                                : couponData.perUserLimit,
                        status: couponData.status || "active",
                    })
                }
            } catch (error) {
                console.error("Error loading coupon data:", error)
                toast.error(
                    isEditMode
                        ? "Failed to load coupon details"
                        : "Failed to load coupon form data",
                )
                if (isEditMode) {
                    navigate("/coupons")
                }
            } finally {
                setFetchLoading(false)
            }
        }

        loadInitialData()
    }, [couponId, isEditMode, navigate])

    const changeHandler = (e) => {
        const { name, value, type, checked } = e.target

        setFormval((prevVal) => {
            if (name === "couponCode") {
                return {
                    ...prevVal,
                    couponCode: value.toUpperCase(),
                }
            }

            if (name === "couponFor") {
                if (value === "all") {
                    return {
                        ...prevVal,
                        couponFor: value,
                        applicableOn: "all",
                        packages: [],
                    }
                }

                return {
                    ...prevVal,
                    couponFor: value,
                    packages: [],
                }
            }

            if (name === "applicableOn") {
                return {
                    ...prevVal,
                    applicableOn: value,
                    packages: value === "selected" ? prevVal.packages : [],
                }
            }

            return {
                ...prevVal,
                [name]: type === "checkbox" ? checked : value,
            }
        })
    }

    const handleTargetSelectionChange = (selectedOptions) => {
        const nextTargetIds = (selectedOptions || []).map((option) => option.value)

        setFormval((prevVal) => ({
            ...prevVal,
            packages: nextTargetIds.map((targetId) =>
                buildTargetEntry(prevVal.couponFor, targetId),
            ),
        }))
    }

    const validateForm = () => {
        const {
            title,
            couponCode,
            discountType,
            discountValue,
            maxDiscount,
            validFrom,
            validTill,
            usageLimit,
            perUserLimit,
            minimumOrderPackage,
        } = formval

        if (!title.trim()) {
            toast.error("Title is required")
            return false
        }

        if (!couponCode.trim()) {
            toast.error("Coupon code is required")
            return false
        }

        if (discountType === "percentage") {
            if (!discountValue || Number(discountValue) <= 0 || Number(discountValue) > 100) {
                toast.error("Discount value must be between 1 and 100 for percentage discount")
                return false
            }

            if (maxDiscount !== "" && Number(maxDiscount) <= 0) {
                toast.error("Maximum discount must be greater than 0")
                return false
            }
        } else if (!discountValue || Number(discountValue) <= 0) {
            toast.error("Discount value must be greater than 0 for flat discount")
            return false
        }

        if (!validFrom) {
            toast.error("Valid From date/time is required")
            return false
        }

        if (!validTill) {
            toast.error("Valid Till date/time is required")
            return false
        }

        if (new Date(validTill) <= new Date(validFrom)) {
            toast.error("Valid Till date must be after Valid From date")
            return false
        }

        if (minimumOrderPackage !== "" && Number(minimumOrderPackage) < 0) {
            toast.error("Minimum order value cannot be negative")
            return false
        }

        if (usageLimit !== "" && Number(usageLimit) <= 0) {
            toast.error("Usage limit must be greater than 0")
            return false
        }

        if (perUserLimit !== "" && Number(perUserLimit) <= 0) {
            toast.error("Per user limit must be greater than 0")
            return false
        }

        if (requiresSpecificSelection && !selectedTargetIds.length) {
            toast.error(`Please select at least one ${selectionItemLabel}`)
            return false
        }

        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        const payload = {
            title: formval.title.trim(),
            couponCode: formval.couponCode.trim().toUpperCase(),
            description: formval.description.trim(),
            discountType: formval.discountType,
            discountValue: Number(formval.discountValue),
            maxDiscount:
                formval.discountType === "percentage" && formval.maxDiscount !== ""
                    ? Number(formval.maxDiscount)
                    : null,
            minimumOrderPackage:
                formval.minimumOrderPackage === ""
                    ? 0
                    : Number(formval.minimumOrderPackage),
            applicableOn: formval.couponFor === "all" ? "all" : formval.applicableOn,
            couponFor: formval.couponFor,
            packages: requiresSpecificSelection ? formval.packages : [],
            validFrom: new Date(formval.validFrom).toISOString(),
            validTill: new Date(formval.validTill).toISOString(),
            usageLimit: formval.usageLimit !== "" ? Number(formval.usageLimit) : null,
            perUserLimit: formval.perUserLimit !== "" ? Number(formval.perUserLimit) : 1,
            status: formval.status,
        }

        try {
            setLoading(true)

            const response = isEditMode
                ? await axios.put(`${baseUrl}/coupon/update/${couponId}`, payload, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token"),
                    },
                })
                : await axios.post(`${baseUrl}/coupon/create`, payload, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token"),
                    },
                })

            if (response.status === 200 || response.status === 201 || response.data?.status) {
                toast.success(
                    isEditMode ? "Coupon updated successfully" : "Coupon created successfully",
                )
                navigate("/coupons")
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? "updating" : "creating"} coupon:`, error)
            toast.error(
                error.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} coupon`,
            )
        } finally {
            setLoading(false)
        }
    }

    if (fetchLoading) {
        return (
            <section className="main-sec">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">
                        {isEditMode ? "Fetching coupon details..." : "Loading..."}
                    </p>
                </div>
            </section>
        )
    }

    return (
        <>
            <style>{`
                .main-sec {
                    background: #f5f7fa;
                    min-height: 100vh;
                    padding: 2rem 0;
                }
                .cards {
                    background: white;
                    border-radius: 12px;
                    padding: 2rem;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
                }
                .form-section-title {
                    color: #7436A9;
                    font-size: 1.15rem;
                    font-weight: 650;
                    margin-bottom: 1.5rem;
                    border-left: 4px solid #7436A9;
                    padding-left: 10px;
                }
            `}</style>

            <section className="main-sec">
                <div className="container">
                    <div className="row align-items-center mb-4">
                        <div className="col-md-6">
                            <div className="dashboard-title">
                                <h4 className="dash-head">
                                    <FontAwesomeIcon icon={faTicketAlt} className="me-2 text-primary" />
                                    {isEditMode ? "Edit Coupon" : "Add New Coupon"}
                                </h4>
                            </div>
                        </div>
                        <div className="col-md-6 text-md-end">
                            <Link to="/coupons" className="btn btn-info text-white">
                                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                                Back
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="cards">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-12">
                                            <h5 className="form-section-title">Coupon Basic Information</h5>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Coupon Code<sup className="text-danger">*</sup></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="couponCode"
                                                placeholder="e.g. WELCOME20"
                                                value={formval.couponCode}
                                                onChange={changeHandler}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Title<sup className="text-danger">*</sup></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                placeholder="e.g. Welcome Discount Offer"
                                                value={formval.title}
                                                onChange={changeHandler}
                                                required
                                            />
                                        </div>

                                        <div className="col-12 mb-4">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                name="description"
                                                rows="3"
                                                placeholder="Enter coupon discount policy or details"
                                                value={formval.description}
                                                onChange={changeHandler}
                                            />
                                        </div>

                                        <div className="col-12">
                                            <h5 className="form-section-title">Discount & Limit Settings</h5>
                                        </div>

                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Discount Type<sup className="text-danger">*</sup></label>
                                            <select
                                                className="form-control"
                                                name="discountType"
                                                value={formval.discountType}
                                                onChange={changeHandler}
                                                required
                                            >
                                                <option value="percentage">Percentage (%)</option>
                                                <option value="flat">Flat Price (Rs.)</option>
                                            </select>
                                        </div>

                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">
                                                Discount Value {formval.discountType === "percentage" ? "(%)" : "(Rs.)"}<sup className="text-danger">*</sup>
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="discountValue"
                                                placeholder={formval.discountType === "percentage" ? "e.g. 20" : "e.g. 200"}
                                                value={formval.discountValue}
                                                onChange={changeHandler}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Maximum Discount (Rs.)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="maxDiscount"
                                                placeholder={formval.discountType === "flat" ? "N/A" : "Leave blank for no limit"}
                                                value={formval.maxDiscount}
                                                onChange={changeHandler}
                                                disabled={formval.discountType === "flat"}
                                            />
                                        </div>

                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Minimum Package Count</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="minimumOrderPackage"
                                                placeholder="e.g. 499"
                                                value={formval.minimumOrderPackage}
                                                onChange={changeHandler}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Total Usage Limit</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="usageLimit"
                                                placeholder="e.g. 500"
                                                value={formval.usageLimit}
                                                onChange={changeHandler}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Per User Limit</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="perUserLimit"
                                                placeholder="e.g. 2"
                                                value={formval.perUserLimit}
                                                onChange={changeHandler}
                                            />
                                        </div>

                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status<sup className="text-danger">*</sup></label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formval.status}
                                                onChange={changeHandler}
                                                required
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>

                                        <div className="col-12">
                                            <h5 className="form-section-title">Validity Period</h5>
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label">Valid From<sup className="text-danger">*</sup></label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                name="validFrom"
                                                value={formval.validFrom}
                                                onChange={changeHandler}
                                                required
                                            />
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label">Valid Till<sup className="text-danger">*</sup></label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                name="validTill"
                                                value={formval.validTill}
                                                onChange={changeHandler}
                                                required
                                            />
                                        </div>

                                        <div className="col-12 mt-2">
                                            <h5 className="form-section-title">Applicability Scope</h5>
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label fw-bold">Coupon For</label>
                                            <select
                                                className="form-control"
                                                name="couponFor"
                                                value={formval.couponFor}
                                                onChange={changeHandler}
                                            >
                                                {COUPON_FOR_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <label className="form-label fw-bold">Applicable On</label>
                                            <select
                                                className="form-control"
                                                name="applicableOn"
                                                value={formval.applicableOn}
                                                onChange={changeHandler}
                                                disabled={formval.couponFor === "all"}
                                            >
                                                {APPLICABLE_ON_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {formval.couponFor === "all" && (
                                                <small className="text-muted">
                                                    Coupon for all selected hai, isliye applicability automatically all rahegi.
                                                </small>
                                            )}
                                        </div>

                                        {requiresSpecificSelection && (
                                            <div className="col-12 mb-4">
                                                <label className="form-label fw-bold">
                                                    Select {selectionLabel} ({selectedTargetIds.length} selected)
                                                </label>
                                                <Select
                                                    isMulti
                                                    options={targetOptions}
                                                    className="basic-multi-select"
                                                    classNamePrefix="select"
                                                    closeMenuOnSelect={false}
                                                    value={selectedTargetOptions}
                                                    onChange={handleTargetSelectionChange}
                                                    placeholder={`Select ${selectionLabel.toLowerCase()}`}
                                                    noOptionsMessage={() => `No ${selectionLabel.toLowerCase()} found`}
                                                />
                                            </div>
                                        )}

                                        <div className="col-12 text-center mt-4">
                                            <button
                                                type="submit"
                                                className="btn btn-for-add text-white px-5 py-2"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon={faSave} className="me-2" />
                                                        {isEditMode ? "Update Coupon" : "Create Coupon"}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
